import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { loadOrgUsers } from '../action/org/load-org-users';
import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../employees/actions/load-org-employees-by-status';
import { userSelector } from '../reducers/current-user-selectors';
import { employeesForOrgIdSelector } from '../reducers/employee-selectors';
import { orgSelector } from '../reducers/org-selectors';
import { usersForOrgIdSelector } from '../reducers/user-selectors';

import { UserForm } from '../admin/user-form';
import { EmployeeUserDialog } from '../employees/employee-user-dialog';
import { UiAutocomplete } from '../ui/ui-autocomplete';
import { UiButton } from '../ui/ui-button';

import { employeeName } from '../employees/utils/employee-name';

type InputProps = {
  label?: string;
  orgId: number;
  value: string | null;
  onChange: (username: string | null) => void;
  autoFocus?: boolean;
  excludeUsernames?: Array<string>;
};

const mapStateToProps = (state: any, { orgId }: InputProps) => ({
  currentUser: userSelector(state),
  org: orgSelector(state, orgId),
  employees: employeesForOrgIdSelector(state, orgId),
  users: usersForOrgIdSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadEmployees: () => dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT)),
    loadUsers: () => dispatch(loadOrgUsers(orgId)),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

const caseAndWhitespaceInsensitiveCompare = (a: string, b: string) =>
  a.toLowerCase().replace(/\s+/g, '') === b.toLowerCase().replace(/\s+/g, '');

export const EmployeeUserSelector = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    org,
    employees,
    users,
    value,
    currentUser,
    onChange,
    actions,
    label,
    autoFocus,
    excludeUsernames,
  }: Props) => {
    const canAddUsers = currentUser.isAdmin || currentUser.isAccountAdmin;

    const [selectedValue, setSelectedValue] = useState<string>('');
    const [isAddingUser, setIsAddingUser] = useState<boolean>(false);
    const [employeeToGrantAccessTo, setEmployeeToGrantAccessTo] = useState<Employee | null>(null);

    useEffect(() => {
      actions.loadEmployees();
      actions.loadUsers();
    });

    if (!employees || !users || !org) {
      return null;
    }

    if (selectedValue === '' && value) {
      const selectedUser = users.find((u) => u.username === value);
      if (selectedUser) {
        setSelectedValue(`user-${value}`);
      }
    }

    const handleOptionClick = (newValue: string) => {
      setSelectedValue(newValue);
      if (newValue.startsWith('user-')) {
        onChange(newValue.replace('user-', ''));
      } else if (newValue.startsWith('employee-')) {
        const selectedEmployeeId = Number.parseInt(newValue.replace('employee-', ''), 10);
        const employee = employees.find((e) => e.employeeId === selectedEmployeeId);
        if (employee) {
          setEmployeeToGrantAccessTo(employee);
        }
      } else {
        onChange(null);
      }
    };

    const handleEmployeeUserCreation = (createdUser?: User) => {
      if (createdUser?.username) {
        onChange(createdUser.username);
        setSelectedValue(`user-${createdUser.username}`);
      } else {
        onChange(null);
        setSelectedValue('');
      }
      setEmployeeToGrantAccessTo(null);
    };

    const handleAddedUser = (user: User) => {
      setIsAddingUser(false);

      onChange(user.username);
      setSelectedValue(`user-${user.username}`);
    };

    const showAddUser = () => setIsAddingUser(true);
    const hideAddUser = () => setIsAddingUser(false);

    let options: Array<SelectOption<string>> = [];
    options = options.concat(
      users
        .filter((u) => u.username && !excludeUsernames?.includes(u.username))
        .map((u) => ({
          label: `${u.firstName} ${u.lastName}`,
          value: `user-${u.username}`,
        })),
    );

    if (canAddUsers) {
      // We also want to include any employees who aren't users if the current user can create users for them.
      // There's a possibility a user and employee aren't linked properly so we do a naive check to make sure the name doesn't match too
      options = options.concat(
        employees
          .filter(
            (e) =>
              e.isCurrentEmployee &&
              !e.username &&
              !users.find((u) =>
                caseAndWhitespaceInsensitiveCompare(employeeName(e), `${u.firstName}${u.lastName}`),
              ),
          )
          .map((e) => ({
            label: employeeName(e),
            value: `employee-${e.employeeId}`,
          })),
      );
    }

    options.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

    return (
      <div className="u-spread">
        <UiAutocomplete
          label={label}
          options={options}
          value={selectedValue}
          onChange={handleOptionClick}
          className="u-width-100"
          autoFocus={autoFocus}
        />
        {canAddUsers && (
          <>
            <UiButton
              className="u-flexShrink-0 u-margin-left"
              disabled={selectedValue !== ''}
              onClick={showAddUser}
            >
              Add User
            </UiButton>
            {isAddingUser ? (
              <UserForm
                onClose={hideAddUser}
                accountId={org.accountId}
                onAddUser={handleAddedUser}
                disableAddAnother
              />
            ) : null}
          </>
        )}
        {employeeToGrantAccessTo ? (
          <EmployeeUserDialog
            onClose={handleEmployeeUserCreation}
            employeeId={employeeToGrantAccessTo.employeeId}
          />
        ) : null}
      </div>
    );
  },
);
